/**
 * Tenant-derived pay bands
 * ============================================================================
 * Generates a list of pay bands per tenant from categoriesOfWorkers, with
 * industry-appropriate salary ranges. Maps category names to department,
 * jobLevel, and currency.
 */

const stableHash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
  return Math.abs(h);
};

/** Industry-appropriate base midpoints (€000) per grade rank 0..7 */
const SALARY_PROFILES = {
  'Professional Services':   [55, 70, 90, 115, 145, 180, 240, 320], // Consulting
  'Consumer Goods & Retail': [38, 48, 60, 80, 105, 140, 190, 280],
  'Financial Services':      [60, 80, 110, 145, 195, 270, 380, 550],
  'Industrial Manufacturing':[42, 52, 65, 85, 110, 145, 195, 280],
  'Security & Facilities Services': [32, 38, 46, 58, 75, 100, 140, 200],
};

const CURRENCY = {
  Germany: 'EUR',
  Spain: 'EUR',
  Switzerland: 'CHF',
  'United Kingdom': 'GBP',
  France: 'EUR',
};

/** Map category name to job level + department slug + grade rank (0=lowest) */
const inferLevel = (categoryName, index, total) => {
  const lower = categoryName.toLowerCase();
  // Grade rank, higher index = more senior typically (categoriesOfWorkers is
  // ordered top-down in tenants.js, so flip it)
  const rank = total - 1 - index;

  let jobLevel = 'mid';
  if (rank >= total - 2) jobLevel = 'executive';
  else if (rank >= total - 4) jobLevel = 'senior';
  else if (rank <= 1) jobLevel = 'junior';

  let department = 'general';
  if (lower.includes('engineer') || lower.includes('r&d') || lower.includes('tech')) department = 'engineering';
  else if (lower.includes('sales')) department = 'sales';
  else if (lower.includes('market') || lower.includes('brand')) department = 'marketing';
  else if (lower.includes('finance') || lower.includes('account')) department = 'finance';
  else if (lower.includes('operations') || lower.includes('manufactur') || lower.includes('production') || lower.includes('supply')) department = 'operations';
  else if (lower.includes('hr') || lower.includes('human')) department = 'hr';
  else if (lower.includes('legal') || lower.includes('compliance')) department = 'legal';
  else if (lower.includes('manage') || lower.includes('director') || lower.includes('lead') || lower.includes('partner') || lower.includes('exec') || lower.includes('vp')) department = 'leadership';

  return { jobLevel, department, rank };
};

const formatDate = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

export function buildPayBands(tenant) {
  const profile = SALARY_PROFILES[tenant.industry] || SALARY_PROFILES['Professional Services'];
  const currency = CURRENCY[tenant.country] || 'EUR';
  const total = tenant.categoriesOfWorkers.length;

  return tenant.categoriesOfWorkers.map((cat, i) => {
    const { jobLevel, department, rank } = inferLevel(cat.name, i, total);
    const profileIdx = Math.min(rank, profile.length - 1);
    const midpoint = profile[profileIdx] * 1000;
    // Range width grows at higher grades
    const width = 0.18 + (rank / total) * 0.22;
    const minSalary = Math.round(midpoint * (1 - width / 2) / 1000) * 1000;
    const maxSalary = Math.round(midpoint * (1 + width / 2) / 1000) * 1000;

    const breach = cat.meanGap > 5;
    const flags = [];
    if (breach) flags.push('gender_gap');
    if (cat.femaleShare < 25 || cat.femaleShare > 75) flags.push('representation');

    const seed = `${tenant.id}-${cat.name}`;
    const annualIncrease = +(3 + (stableHash(seed) % 25) / 10).toFixed(1); // 3.0–5.5

    return {
      id: i + 1,
      name: cat.name,
      department,
      jobLevel,
      minSalary,
      maxSalary,
      currency,
      effectiveDate: '2025-01-01',
      endDate: '',
      status: 'active',
      employeeCount: cat.headcount,
      femaleShare: cat.femaleShare,
      meanGap: cat.meanGap,
      description: `${cat.name} compensation band at ${tenant.name}. Range reflects internal job architecture and external market benchmarks for ${tenant.industry} sector in ${tenant.country}.`,
      progressionRules: {
        annualIncrease,
        maxProgression: 15 + (rank * 2),
        performanceMultiplier: 1.2 + (rank * 0.05),
      },
      complianceSettings: {
        autoFlag: true,
        gapThreshold: 5,
        reviewFrequency: rank >= total - 3 ? 'quarterly' : 'annual',
      },
      complianceFlags: flags,
    };
  });
}
